<script lang="ts" setup>
import { computed } from "vue";

import { useArticleFont } from "@/common/composables/use-article-font.composable";
import ArabicExplanationsCell from "@/components/article/arabic/ArabicExplanationsCell.vue";
import ArabicLemmaCell from "@/components/article/arabic/ArabicLemmaCell.vue";
import DanishExplanationsCell from "@/components/article/arabic/DanishExplanationsCell.vue";
import DanishLemmaCell from "@/components/article/arabic/DanishLemmaCell.vue";
import type { DanishArabicRows } from "@/components/article/arabic/response.interface";
import { parseRows } from "@/components/article/arabic/response-parser";
import ArticleContent from "@/components/article/ArticleContent.vue";

const props = defineProps<{
  articles: DanishArabicRows[];
}>();

const { arabicArticleFontClass } = useArticleFont();
const groupedArticles = computed(() => parseRows(props.articles));
</script>

<template>
  <ArticleContent>
    <div v-for="(article, articleIndex) in groupedArticles" :key="articleIndex">
      <div class="row no-gutters">
        <div
          class="col-12 col-lg-6 order-1 article-column article-column--first"
          :class="{ 'article-column--top-left': articleIndex === 0 }"
        >
          <div class="article-cell">
            <DanishLemmaCell :lemma="article.lemma.danish" />
          </div>
        </div>
        <div
          class="col-12 col-lg-6 order-3 order-lg-2 article-column"
          :class="{ 'article-column--top-right': articleIndex === 0 }"
        >
          <div class="article-cell article-cell--rtl">
            <ArabicLemmaCell :lemma="article.lemma.arabic" />
          </div>
        </div>

        <template v-if="article.explanations.hasAny()">
          <div
            v-if="article.explanations.hasDanish()"
            class="col-12 order-2 order-lg-3 article-column"
            :class="{ 'col-lg-6 article-column--first': article.explanations.hasArabic() }"
          >
            <div class="article-cell article-cell--indent">
              <DanishExplanationsCell :explanations="article.explanations.danish" />
            </div>
          </div>
          <div
            v-if="article.explanations.hasArabic()"
            class="col-12 order-4 article-column"
            :class="{ 'col-lg-6': article.explanations.hasDanish() }"
          >
            <div class="article-cell article-cell--indent article-cell--rtl">
              <ArabicExplanationsCell :explanations="article.explanations.arabic" />
            </div>
          </div>
        </template>
      </div>

      <div
        v-for="(example, exampleIndex) in article.examples"
        :key="exampleIndex"
        class="row no-gutters"
      >
        <div class="col-12 col-lg-6 article-column article-column--first">
          <div
            class="article-cell"
            :class="{
              'article-cell--indent': !article.explanations.hasAny(),
              'article-cell--double-indent': article.explanations.hasAny()
            }"
            lang="da"
          >
            {{ example.danish || "~" }}
          </div>
        </div>

        <div class="col-12 col-lg-6 article-column">
          <div
            class="article-cell article-cell--rtl"
            :class="[
              {
                'article-cell--indent': !article.explanations.hasAny(),
                'article-cell--double-indent': article.explanations.hasAny()
              },
              arabicArticleFontClass
            ]"
            lang="ar"
            dir="rtl"
          >
            {{ example.arabic || "~" }}
          </div>
        </div>
      </div>
    </div>
  </ArticleContent>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

@grey-50-color: rgb(@theme-colors[@grey][50]);

.article-column {
  border-top: 1px solid @grey-50-color;

  &--top-left {
    border-top: none;
  }

  &--top-right {
    #media-breakpoint-up(lg, {
      border-top: none;
    });
  }

  &--first {
    #media-breakpoint-up(lg, {
      border-right: 1px solid @grey-50-color;
    });
  }
}

.article-cell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-height: 55px;
  padding-top: 12px;
  padding-right: 20px;
  padding-bottom: 12px;
  padding-left: 20px;
  gap: 5px 15px;

  &--indent {
    #media-breakpoint-up(lg, {
      padding-right: 40px;
      padding-left: 40px;
    });
  }

  &--double-indent {
    #media-breakpoint-up(lg, {
      padding-right: 60px;
      padding-left: 60px;
    });
  }

  &--rtl {
    justify-content: right;
    text-align: right;
  }
}
</style>
